/**
 * Explain primitives — the "verständliche Bodenblick" toolkit.
 *
 * <ExplainPanel> ............. inline accordion under a section, opens to
 *                              reveal a layman-friendly explanation.
 * <MetricTooltip> ............ wraps a number/label and shows a one-line
 *                              tooltip on hover/tap (mobile-friendly).
 * <StoryCard> ................ a "So liest du das"-style storytelling card
 *                              used at the top of each major page to set
 *                              the context in 1 sentence + 3 bullets.
 *
 * All components avoid jargon, use Cabinet Grotesk for headlines,
 * JetBrains Mono Local for labels. Designed to be sprinkled in
 * sparingly — info density should never overwhelm the page.
 */
import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Info, CaretDown, BookOpen, Lightbulb, Sparkle } from "@phosphor-icons/react";

/* ---------------------------------------------------------------- *
 * ExplainPanel — collapsible "Was bedeutet das?"-section.
 * ---------------------------------------------------------------- */
export function ExplainPanel({
  title = "Was bedeutet das?",
  testid = "explain-panel",
  defaultOpen = false,
  accent = "#00FA9A",
  children,
  className = "",
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={`border border-white/15 bg-black/50 backdrop-blur-sm overflow-hidden ${className}`}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2.5 px-4 py-3 text-left hover:bg-[#121212] transition-colors group"
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <BookOpen size={14} weight="fill" style={{ color: accent }} />
        <span className="flex-1 text-[12px] uppercase tracking-[0.18em] font-mono text-white/80">
          {title}
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25 }}
          style={{ color: accent }}
        >
          <CaretDown size={12} weight="bold" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="border-t border-white/15"
          >
            <div className="px-4 py-4 text-sm text-white/80 leading-relaxed space-y-3 font-display-body">
              {children}
              <div className="pt-2 mt-1 border-t border-white/15">
                <Link
                  to="/glossar"
                  className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-mono hover:underline"
                  style={{ color: accent }}
                  data-testid={`${testid}-glossary-link`}
                >
                  <BookOpen size={11} weight="fill" /> Mehr im Glossar
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------------------------------------------------------- *
 * MetricTooltip — show a short explanation when the user hovers
 * (desktop) or taps (mobile) a number. Uses native <title> as a
 * keyboard/SR-friendly fallback.
 * ---------------------------------------------------------------- */
export function MetricTooltip({ explain, children, accent = "#00FA9A", className = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className={`relative inline-flex items-center gap-1.5 ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onClick={() => setOpen((v) => !v)}
      title={explain}
    >
      {children}
      <Info size={11} className="opacity-50 group-hover:opacity-100 transition" style={{ color: accent }} />
      <AnimatePresence>
        {open && (
          <motion.span
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18 }}
            className="absolute left-1/2 top-full -translate-x-1/2 mt-1.5 w-64 px-3 py-2 bg-[#F5F5F5] text-[#0A0A0A] text-[12px] leading-snug rounded-sm z-30 shadow-[0_0_0_1px_rgba(255,255,255,0.06)] font-normal pointer-events-none"
            role="tooltip"
          >
            {explain}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

/* ---------------------------------------------------------------- *
 * StoryCard — "So liest du diese Seite" hero block, placed at the
 * top of every major data page. 1 lead sentence + up to 3 bullets.
 * ---------------------------------------------------------------- */
export function StoryCard({
  eyebrow = "So liest du das",
  title,
  lead,
  bullets = [],
  accent = "#00FA9A",
  icon: Icon = Lightbulb,
  testid = "story-card",
  className = "",
}) {
  return (
    <div
      className={`relative overflow-hidden border border-white/15 bg-[#121212] ${className}`}
      style={{ borderLeftWidth: 4, borderLeftColor: accent }}
      data-testid={testid}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-mono" style={{ color: accent }}>
          <Icon size={12} weight="fill" /> {eyebrow}
        </div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tighter mt-2 text-white leading-[1.05]">
          {title}
        </h2>
        {lead && (
          <p className="text-sm sm:text-base text-white/75 leading-relaxed mt-3 max-w-2xl">
            {lead}
          </p>
        )}
        {bullets.length > 0 && (
          <ul className="mt-4 grid sm:grid-cols-3 gap-3">
            {bullets.map((b, i) => (
              <li key={i} className="text-[13px] text-white/80 leading-snug flex gap-2">
                <Sparkle size={12} weight="fill" className="mt-0.5 shrink-0" style={{ color: accent }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
